RivalIQ.module('state', function (state, App, Backbone, Marionette, $, _) {

    'use strict';

    const utils = state.utils = {};

    /**
     * Persists state.
     * @param map
     * @param state
     */
    utils.persistState = function (type, map, viewState) {
        App.execute(`persistence:${type}:save`, function (state) {

            const data = {};
            const properties = viewState.toJSON();
            _.each(map, function (config) {
                if (_.has(config, 'persistenceProperty')) {
                    if (_.has(config, 'persistenceSerializer')) {
                        data[config.persistenceProperty] = config.persistenceSerializer(properties, state);
                        return;
                    }

                    const stateProperty = (_.has(config, 'stateProperty') ? config.stateProperty : config.settingProperty);
                    if (_.has(properties, stateProperty)) {
                        data[config.persistenceProperty] = properties[stateProperty];
                    }
                }
            });

            return data;
        }, viewState);
    };

    /**
     * Initializes state from properties.
     * @param type
     * @param map
     * @param state
     * @param properties
     */
    utils.initializeState = function (type, path, map, state, properties) {
        const data = _.reduce(map, function (memo, config) {
            const propPath = (_.has(config, 'path') ? config.path : path);

            const root = (propPath ? properties[propPath] : properties);

            const stateProperty = (_.has(config, 'stateProperty') ? config.stateProperty : config.settingProperty);

            if (_.has(root, config.settingProperty)) {

                memo[stateProperty] = _.get(root, config.settingProperty);
            } else if (_.has(config, 'default')) {

                memo[stateProperty] = config.default;
            }

            if (config.immutable !== true) {
                // wire changes back to settings for free
                state.on(`change:${stateProperty}`, function (model) {
                    if (propPath) {

                        App.settings[propPath] = (App.settings[propPath] || {});
                    }

                    let value = model.get(stateProperty);

                    if (config.serializer) {
                        value = config.serializer(value);
                        state.set(stateProperty, value, {
                            silent: true
                        });
                    }

                    const settingsPath = (propPath ? App.settings[propPath] : App.settings);
                    settingsPath[config.settingProperty] = value;

                    // NOTE only persist state if a type has been specified
                    // otherwise we have no strategy for persistence...this
                    // ends up being useful for immutable app concepts
                    // (e.g., an account id)
                    if (type) {
                        utils.persistState(type, map, state);
                    }
                });
            }

            return memo;
        }, {});

        state.set(data, {
            silent: true
        });
    };

    (function () {

        /*
         * Find cyclic references within the specified object.
         * @param root
         * @param name (optional)
         * @returns array of references
         */
        function findObjectCycles(root, name) {
            const nodes = [];
            const path = [];
            const cyclicPaths = [];

            function helper(obj) {
                if (_.isObject(obj)) {
                    nodes.push({
                        path: path.join('.'),
                        obj: obj
                    });

                    const keys = Object.keys(obj);

                    for (let i = 0; i < keys.length; i++) {
                        const key = keys[i];
                        const value = obj[key];

                        path.push(key);

                        const found = _.find(nodes, 'obj', value);

                        if (found) {
                            cyclicPaths.push({
                                src: path.join('.'),
                                ref: found.path
                            });
                        } else {
                            helper(value);
                        }

                        path.pop();
                    }

                    nodes.pop();
                }
            }

            if (name) {
                path.push(name);
            }

            helper(root);
            return cyclicPaths;
        }

        /**
         * Clones via JSON stringify and parse to circumvent reference issues.
         * @param value
         * @returns {*}
         */
        function clone(value, name) {

            if (!_.isUndefined(value)) {

                try {
                    // NOTE: moment will convert to UTC date string in JSON.stringify
                    // overwrite these UTC strings with their original format
                    const dates = _.reduce(value, (memo, val, key) => {
                        if (moment.isMoment(val)) {
                            memo[key] = val.format('YYYY-MM-DDTHH:mm:ss');
                        }
                        return memo;
                    }, {});
                    const cloned = JSON.parse(JSON.stringify(value));
                    _.each(dates, (val, key) => {
                        cloned[key] = val;
                    });
                    return cloned;
                } catch (err) {
                    if (err.message === 'Converting circular structure to JSON') {
                        throw new Error(findObjectCycles(value, name));
                    }

                    // Always rethrow the error because we want to fail loudly
                    throw (err);
                }
            }
        }

        /**
         * Overrides the default implementation of get to allow
         * for deserialization of property values.
         * @param state
         * @param map
         */
        utils.overrideStateGet = function (state, map) {

            const getter = state.get;

            state.get = function (key) {

                // todo: map in this case should probably be a state property map
                const deserializer = (map && map[key]);
                const value = getter.apply(state, arguments);
                // NOTE cloning values only prevents reference equality problems on objects
                const result = (deserializer ? deserializer.call(state, value) : clone(value, key));
                return result;
            };
        };

        /**
         * Overrides the default implementation of set to prevent
         * reference equality from interfering with persistence.
         * @param state
         */
        utils.overrideStateSet = function (state, map) {

            const setter = state.set;

            state.set = function (key, val, options) {

                let values;

                if (key === null) {

                    return this;
                }

                // Handle both `"key", value` and `{key: value}` -style arguments.
                if (typeof key === 'object') {

                    values = key;
                    options = val;
                } else {

                    (values = {})[key] = val;
                }

                const attributes = {};
                _.each(values, function (value, key) {
                    const serializer = (map && map[key]);
                    attributes[key] = (serializer ? serializer.call(this, value) : clone(value, key));
                });

                return setter.apply(state, [attributes, options]);
            };
        };
    }());

    /**
     * Binds state events (view, landscape) to an entity.
     * @param entity
     * @param configuration
     * @param [stateEventMap]
     */
    utils.bindStateEvents = function (entity, configuration, stateEventMap) {

        const map = _.defaults({
            user: state.user,
            view: state.view,
            landscape: state.landscape,
            dashboard: state.dashboards
        }, stateEventMap);

        _.each(map, function (state, name) {

            const stateEventName = `${name}StateEvents`;
            const stateEvents = _.result((configuration ? configuration : entity), stateEventName);

            if (stateEvents) {

                Marionette.bindEntityEvents(entity, state, stateEvents);
            }
        });
    };

    utils.getCompanyIdFromSetId = function (companySetId) {
        return RivalIQ.state.utils.isOnlyCompanySet(companySetId) && parseInt(companySetId.replace(state.companySets.onlyCompany, ''));
    };

    utils.getFocusedCompanyId = function () {
        if (utils.isOnlyCompanySet(state.view.get('companySetDescriptor'))) {
            return utils.getCompanyIdFromSetId(state.view.get('companySetDescriptor'));
        }
        return state.view.get('focusCompanyId');
    };

    utils.isFocusedCompany = function (companyId) {
        const focusedCompanyId = utils.getFocusedCompanyId();
        return focusedCompanyId === companyId;
    };

    utils.getCompanySetName = function (companySetId) {
        if (utils.isOnlyCompanySet(companySetId)) {
            const companyId = utils.getCompanyIdFromSetId(companySetId);
            return `Only ${RivalIQ.state.landscape.getCompanyById(companyId)?.get('name')}`;
        }
        return state.companySets.get(companySetId)?.get('name');
    };

    utils.isOnlyCompanySet = function (companySetId) {
        return _.startsWith(companySetId, state.companySets.onlyCompany);
    };
});
